// hooks and context
import { useSideEditorContext } from '@components/SideEditor/utils';
import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
import useNode from '@hooks/use-node';
// utils
import Emitter from '@modules/event-emitter';
import { type FC, useContext, useEffect } from 'react';

// types
import { SideEditorNodeKey } from '../../types';
// components
import EmailSignatureEditorPanelSection, {
  EmailSignatureEditorEmptyPanelSection,
  EmailSignatureEditorPanelSectionContents,
  EmailSignatureEditorPanelSectionHeading,
} from '../EmailSignatureEditorPanelSection';
import EmailSignatureSocialFieldContainer from './EmailSignatureEditorSocialFieldContainer';
import styles from './EmailSignatureEditorSocialLinksSection.module.scss';

const EmailSignatureEditorSocialLinksSection: FC<React.PropsWithChildren<unknown>> = () => {
  const { emailSignature, isInTransitionState, isSignatureLocked } = useContext(
    emailSignatureSideEditorServicesContext,
  );
  const [socialLinksSectionDiv, socialLinksSectionDivRef] = useNode<HTMLDivElement>();
  const { changeTab, registerNode, panelIndex } = useSideEditorContext();

  useEffect(() => {
    if (!socialLinksSectionDiv) return;
    registerNode(SideEditorNodeKey.SOCIAL_LINKS_NODE, {
      panelBelongTo: panelIndex,
      node: socialLinksSectionDiv,
    });
  }, [socialLinksSectionDiv, panelIndex, registerNode]);

  useEffect(() => {
    const handleScrollToSocialLinks = () => {
      if (isInTransitionState) {
        return;
      }

      changeTab(panelIndex);
    };
    Emitter.on('SCROLL_TO_SOCIAL_LINKS_SECTION', handleScrollToSocialLinks);

    return () => {
      Emitter.off('SCROLL_TO_SOCIAL_LINKS_SECTION', handleScrollToSocialLinks);
    };
  }, [changeTab, isInTransitionState, panelIndex]);
  if (emailSignature.socialLinksOrder.length > 0) {
    return (
      <EmailSignatureEditorPanelSection ref={socialLinksSectionDivRef}>
        <EmailSignatureEditorPanelSectionHeading heading="Links" />
        <EmailSignatureEditorPanelSectionContents>
          {emailSignature.socialLinksOrder.map((socialLinkId) => (
            <EmailSignatureSocialFieldContainer
              isLocked={isSignatureLocked}
              key={socialLinkId}
              {...emailSignature.socialLinks[socialLinkId]}
            />
          ))}
        </EmailSignatureEditorPanelSectionContents>
      </EmailSignatureEditorPanelSection>
    );
  } else {
    return (
      <EmailSignatureEditorEmptyPanelSection ref={socialLinksSectionDivRef}>
        <EmailSignatureEditorPanelSectionHeading heading="Links" />
        <EmailSignatureEditorPanelSectionContents>
          <div className={styles['add-new-links-message']}>Click the buttons below to add new links</div>
        </EmailSignatureEditorPanelSectionContents>
      </EmailSignatureEditorEmptyPanelSection>
    );
  }
};

export default EmailSignatureEditorSocialLinksSection;
